import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { applySeo } from "@/lib/seo";

const credentialsSchema = z.object({
  email: z.string().trim().email({ message: "Enter a valid email." }).max(255),
  password: z.string().min(8, { message: "Min 8 characters." }).max(72),
});

const Auth = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = (location.state as { from?: string } | null)?.from ?? "/admin/clinic-settings";

  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    applySeo({
      title: "Admin sign in — Dr Vahe Clinic",
      description:
        "Restricted sign-in area for Dr Vahe Clinic administrators to manage clinic settings, embed snippets, and GBP content.",
      path: "/auth",
    });
    let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");

    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate(redirectTo, { replace: true });
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) navigate(redirectTo, { replace: true });
    });

    return () => {
      sub.subscription.unsubscribe();
      robots?.setAttribute("content", "index, follow");
    };
  }, [navigate, redirectTo]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = credentialsSchema.safeParse({ email, password });
    if (!parsed.success) {
      toast({
        title: "Check your details",
        description: parsed.error.issues[0]?.message ?? "Invalid input",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    try {
      const creds = { email: parsed.data.email!, password: parsed.data.password! };
      if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword(creds);
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signUp({
          ...creds,
          options: { emailRedirectTo: `${window.location.origin}/auth` },
        });
        if (error) throw error;
        toast({
          title: "Check your inbox",
          description: "Confirm your email, then sign in.",
        });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong";
      toast({ title: "Authentication failed", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="text-[10px] tracking-[0.3em] uppercase text-accent hover:text-accent/70"
        >
          ← Back to clinic
        </Link>
        <h1 className="font-display text-3xl mt-6 mb-2 text-foreground">
          Admin <span className="italic">access</span>
        </h1>
        <p className="text-sm text-muted-foreground font-light mb-8">
          Restricted area. Sign in to manage clinic settings.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
              Email
            </span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-accent/40"
            />
          </label>
          <label className="block">
            <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
              Password
            </span>
            <input
              type="password"
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-accent/40"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            className="w-full px-7 py-3 bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.3em] rounded-full hover:bg-accent transition-colors duration-500 disabled:opacity-50"
          >
            {loading ? "Working…" : mode === "signin" ? "Sign in" : "Create account"}
          </button>
        </form>

        <button
          type="button"
          onClick={() => setMode((m) => (m === "signin" ? "signup" : "signin"))}
          className="mt-6 text-xs text-muted-foreground hover:text-accent transition-colors"
        >
          {mode === "signin"
            ? "No account yet? Create one"
            : "Already have an account? Sign in"}
        </button>
      </div>
    </div>
  );
};

export default Auth;
